<template>
  <div class="ai-agent-container" style="position: relative; height: 100%; padding: 10px">
    <el-dialog
      v-loading="dialogLoading"
      :top="dialogTopMargin"
      :title="(form.id ? '編輯' : '新增') + funcName"
      :visible.sync="dialogVisible"
      width="50%"
    >
      <el-card class="box-card">
        <el-form ref="form" :model="form" :label-width="formLabelWidth">
          <el-form-item
            label="場域名稱"
            prop="siteName"
            :rules="[{ required: true, message: '請輸入場域名稱', trigger: 'blur' }]"
          >
            <el-input v-model="form.siteName" />
          </el-form-item>

          <el-form-item
            label="場域代碼"
            prop="code"
            :rules="[{ required: true, message: '請輸入場域代碼', trigger: 'blur' }]"
          >
            <el-input v-model="form.code" />
            <i>產生金鑰的代碼</i>
          </el-form-item>

          <el-form-item label="場域金鑰(存檔後產生)" prop="secret">
            <el-input v-model="form.secret" readonly />
          </el-form-item>

          <el-form-item
            label="Assistants ID"
            prop="assistantsID"
            :rules="[{ required: true, message: '請輸入 Assistants ID', trigger: 'blur' }]"
          >
            <el-input v-model="form.assistantsID" />
          </el-form-item>

          <el-form-item label="狀態">
            <el-switch v-model="form.isEnabled" active-text="啟用" inactive-text="停用" />
          </el-form-item>

          <el-divider content-position="left">虛擬人介面設定</el-divider>

          <el-form-item label="靜態圖片 URL">
            <el-input v-model="form.idleImageUrl" placeholder="JPG / PNG / GIF 網址" />
          </el-form-item>

          <el-form-item label="開場影片 URL">
            <el-input v-model="form.openingVideoUrl" placeholder="進站自動播放影片網址" />
          </el-form-item>

          <el-form-item label="回答影片 URL">
            <el-input v-model="form.replyVideoUrl" placeholder="AI 回答中循環影片網址" />
          </el-form-item>
        </el-form>
      </el-card>

      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="saveBasicData">確 定</el-button>
      </span>
    </el-dialog>

    <el-dialog
      v-loading="dialogLoading"
      :title="'聊天室設定 - ' + (form.siteName || '未命名場域')"
      :visible.sync="chatDialogVisible"
      width="80%"
      top="3vh"
    >
      <el-alert
        title="所有欄位皆為選填；未填時前台將沿用目前預設內容。"
        type="info"
        :closable="false"
        show-icon
      />

      <div class="chatroom-grid">
        <div class="chatroom-grid__header header-item">項目</div>
        <div
          v-for="lang in languageOptions"
          :key="'header-' + lang.code"
          class="chatroom-grid__header"
        >
          {{ lang.label }}
        </div>

        <template v-for="row in chatroomRows">
          <div :key="row.key + '-label'" class="chatroom-grid__label">
            {{ row.label }}
          </div>
          <div
            v-for="lang in languageOptions"
            :key="row.key + '-' + lang.code"
            class="chatroom-grid__cell"
          >
            <el-input
              v-model="row.model[lang.code]"
              :type="row.type || 'text'"
              :rows="row.rows || 2"
              resize="vertical"
              :placeholder="lang.label"
            />
          </div>
        </template>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="chatDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="saveChatroomData">儲 存</el-button>
      </span>
    </el-dialog>

    <el-dialog
      v-loading="dialogLoading"
      :top="dialogTopMargin"
      :title="'查看紀錄'"
      :visible.sync="dialogVisibleByChatLog"
      width="70%"
    >
      <el-card class="box-card" style="overflow: auto; height: calc(70vh + 15px)">
        <el-table
          v-loading="listLoading"
          size="mini"
          :data="listByChatLog"
          border
          fit
          highlight-current-row
          height="calc(70vh - 80px)"
          overflow-x="auto"
        >
          <el-table-column align="center" label="項次" width="60">
            <template slot-scope="scope">
              <span>{{ scope.$index + 1 }}</span>
            </template>
          </el-table-column>

          <el-table-column align="left" label="QA的日期時間" min-width="140">
            <template slot-scope="scope">
              <span>{{ scope.row.createdTime }}</span>
            </template>
          </el-table-column>

          <el-table-column align="left" label="Q的問題" min-width="250">
            <template slot-scope="scope">
              <span>{{ scope.row.questions }}</span>
            </template>
          </el-table-column>

          <el-table-column align="left" label="A的答案" min-width="250">
            <template slot-scope="scope">
              {{ scope.row.answers }}
            </template>
          </el-table-column>
        </el-table>

        <pagination
          v-show="total > 0"
          layout="prev, pager, next"
          :total="total"
          :page.sync="listQueryByChatLog.page"
          :limit.sync="listQueryByChatLog.limit"
          @pagination="getChatLogsByAIAgentId(dialogRowIdByChatLog)"
        />
      </el-card>

      <span slot="footer" class="dialog-footer">
        <el-button
          :disabled="listLoading"
          type="success"
          @click="exportToExcelByChatLog(dialogRowIdByChatLog)"
          >下載Excel</el-button
        >
        <el-button :disabled="listLoading" @click="dialogVisibleByChatLog = false">關 閉</el-button>
      </span>
    </el-dialog>

    <el-dialog
      v-loading="listLoading"
      :title="'虛擬人介面設定'"
      :visible.sync="dialogVisibleByDefaultSetting"
      width="60%"
    >
      <el-card class="box-card">
        <el-form ref="form" :model="formByDefaultSetting">
          <el-form-item label="靜態圖片 URL">
            <el-input
              v-model="formByDefaultSetting.idleImageUrl"
              placeholder="JPG / PNG / GIF 網址"
            />
          </el-form-item>

          <el-form-item label="開場影片 URL">
            <el-input
              v-model="formByDefaultSetting.openingVideoUrl"
              placeholder="進站自動播放影片網址"
            />
          </el-form-item>

          <el-form-item label="回答影片 URL">
            <el-input
              v-model="formByDefaultSetting.replyVideoUrl"
              placeholder="AI 回答中循環影片網址"
            />
          </el-form-item>

          <el-form-item label="APP AI">
            <el-input v-model="formByDefaultSetting.appAiUrl" placeholder="APP AI網址" />
          </el-form-item>

          <el-form-item label="WEB AI">
            <el-input v-model="formByDefaultSetting.webAiUrl" placeholder="WEB AI網址" />
          </el-form-item>
        </el-form>
      </el-card>
      <span slot="footer" class="dialog-footer">
        <el-button type="success" :disabled="listLoading" @click="saveByDefaultSetting">
          保 存
        </el-button>
        <el-button type="danger" :disabled="listLoading" @click="closeByDefaultSetting">
          關 閉
        </el-button>
      </span>
    </el-dialog>

    <div style="padding: 20px; padding-bottom: 0">
      <el-form style="display: flex; align-items: flex-end; gap: 0 8px" label-position="left">
        <el-form-item label="場域查詢">
          <el-input v-model="listQuery.search" placeholder="輸入場域名稱" />
        </el-form-item>

        <el-form-item label="狀態">
          <el-select v-model="listQuery.status" clearable placeholder="請選擇狀態">
            <el-option label="啟用" :value="true" />
            <el-option label="停用" :value="false" />
          </el-select>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" @click="getList">查詢</el-button>
        </el-form-item>

        <el-form-item>
          <el-button
            style="cursor: pointer"
            :loading="listLoading"
            type="success"
            icon="el-icon-refresh-left"
            circle
            @click="refresh"
          />
        </el-form-item>

        <el-form-item>
          <el-button type="success" @click="handleCreate">新增{{ funcName }}</el-button>
        </el-form-item>

        <el-form-item>
          <el-button type="warning" @click="handleDefaultSetting">預設虛擬人介面</el-button>
        </el-form-item>
      </el-form>
    </div>

    <div style="position: relative; height: 80%">
      <el-table
        v-loading="listLoading"
        size="mini"
        :data="list"
        border
        fit
        highlight-current-row
        height="100%"
      >
        <el-table-column align="center" label="項次" width="60">
          <template slot-scope="scope">
            <span>{{ scope.$index + 1 }}</span>
          </template>
        </el-table-column>

        <el-table-column align="center" label="場域名稱" min-width="200">
          <template slot-scope="scope">
            <span>{{ scope.row.siteName }}</span>
          </template>
        </el-table-column>

        <el-table-column align="center" label="場域代碼" min-width="100">
          <template slot-scope="scope">
            <span>{{ scope.row.code }}</span>
          </template>
        </el-table-column>

        <el-table-column align="center" label="場域金鑰(存檔後產生)" min-width="300">
          <template slot-scope="scope">
            <el-tag effect="dark" type="danger">{{ scope.row.secret }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column align="center" label="Assistants ID" min-width="300">
          <template slot-scope="scope">
            <el-tag effect="dark" type="info">{{ scope.row.assistantsID }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column align="center" label="狀態" width="100">
          <template slot-scope="scope">
            <el-tag :type="scope.row.isEnabled ? 'success' : 'danger'">
              {{ scope.row.isEnabled ? '啟用' : '停用' }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column align="center" label="建立日期" width="160">
          <template slot-scope="scope">
            <span>{{ scope.row.createDate }}</span>
          </template>
        </el-table-column>

        <el-table-column align="center" label="更新日期" width="160">
          <template slot-scope="scope">
            <span>{{ scope.row.updateDate }}</span>
          </template>
        </el-table-column>

        <el-table-column align="center" label="操作" width="360">
          <template slot-scope="scope">
            <el-button size="mini" type="warning" @click="handleChatLog(scope.row)"
              >查看紀錄</el-button
            >
            <el-button size="mini" type="primary" @click="handleEdit(scope.row)">編輯</el-button>
            <el-button size="mini" type="success" @click="handleChatroom(scope.row)"
              >聊天室設定</el-button
            >
            <el-button size="mini" type="danger" @click="handleDelete(scope.row)">刪除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <pagination
      v-show="total > 0"
      style="padding-top: 0"
      layout="prev, pager, next"
      :total="total"
      :page.sync="listQuery.page"
      :limit.sync="listQuery.limit"
      @pagination="getList"
    />
  </div>
</template>

<script>
import Pagination from '@/components/Pagination'
import { MessageBox, Message } from 'element-ui'
import {
  getAiAgentList,
  saveAiAgent,
  deleteAiAgent,
  getAiAgentChatLogForPaged,
  downloadAiAgentChatLogsToExcel,
  saveAiAgentDefaultSetting,
  getAiAgentDefaultSetting,
} from '@/api/aiagent'

const languageOptions = [
  { code: 'zh-TW', label: '繁中' },
  { code: 'zh-CN', label: '簡中' },
  { code: 'ja-JP', label: '日文' },
  { code: 'ko-KR', label: '韓文' },
  { code: 'th-TH', label: '泰文' },
  { code: 'id-ID', label: '印尼' },
  { code: 'es-ES', label: '西文' },
  { code: 'en-US', label: '英文' },
  { code: 'de-DE', label: '德文' },
  { code: 'fr-FR', label: '法文' },
  { code: 'it-IT', label: '義文' },
]

const defaultWelcomeMessages = {
  'zh-TW': '您好，我是AI虛擬解說員，歡迎向我提問關於這個地方的任何問題~',
  'zh-CN': '您好，我是AI虚拟解说员，欢迎向我提问关于这个地方的任何问题~',
  'ja-JP': 'こんにちは、AIバーチャルガイドです。この場所について何でもお尋ねください~',
  'ko-KR': '안녕하세요, AI 가상 안내원입니다. 이 장소에 대해 궁금한 점을 물어보세요~',
  'th-TH': 'สวัสดี ฉันเป็นผู้บรรยายเสมือนจริง AI ยินดีตอบคำถามเกี่ยวกับสถานที่นี้~',
  'id-ID': 'Halo, saya adalah pemandu virtual AI. Silakan tanyakan apa saja tentang tempat ini~',
  'es-ES': 'Hola, soy un guia virtual con IA. Puedes preguntarme cualquier cosa sobre este lugar~',
  'en-US': 'Hello, I am an AI virtual guide. Feel free to ask me any questions about this place~',
  'de-DE': 'Hallo, ich bin ein virtueller KI-Guide. Fragen Sie mich gerne alles ueber diesen Ort~',
  'fr-FR':
    "Bonjour, je suis votre guide virtuel IA. N'hesitez pas a me poser des questions sur cet endroit~",
  'it-IT': 'Ciao, sono una guida virtuale AI. Sentiti libero di farmi domande su questo posto~',
}

function createLocalizedMap() {
  return languageOptions.reduce((acc, item) => {
    acc[item.code] = ''
    return acc
  }, {})
}

function createSuggestedQuestions() {
  return Array.from({ length: 5 }, (_, index) => ({
    order: index + 1,
    translations: createLocalizedMap(),
  }))
}

function createDefaultForm() {
  return {
    id: null,
    siteName: '',
    code: '',
    secret: '',
    apiKey: '',
    assistantsID: '',
    idleImageUrl: '',
    openingVideoUrl: '',
    replyVideoUrl: '',
    welcomeMessages: createLocalizedMap(),
    suggestedQuestions: createSuggestedQuestions(),
    isEnabled: true,
  }
}

function cloneDeep(value) {
  return JSON.parse(JSON.stringify(value))
}

export default {
  name: 'AiAgentManagement',
  components: {
    Pagination,
  },
  data() {
    return {
      funcName: 'AI Agent',
      dialogTopMargin: '5%',
      formLabelWidth: '120px',
      form: createDefaultForm(),
      dialogLoading: false,
      dialogVisible: false,
      chatDialogVisible: false,
      listLoading: false,
      listQuery: {
        search: '',
        status: null,
        page: 1,
        limit: 20,
        sort: '-id',
      },
      total: 0,
      list: [],
      languageOptions,
      listByChatLog: [],
      dialogVisibleByChatLog: false,
      dialogRowIdByChatLog: null,
      listQueryByChatLog: {
        page: 1,
        limit: 10,
      },
      dialogVisibleByDefaultSetting: false,
      formByDefaultSetting: {
        idleImageUrl: '',
        openingVideoUrl: '',
        replyVideoUrl: '',
        appAiUrl: '',
        webAiUrl: '',
      },
    }
  },
  watch: {
    async listLoading(newValue) {
      if (newValue) {
        try {
          await this.$store.dispatch('app/showDownloadLoading', true)
          await this.$store.dispatch('app/showDownloadLoadingText', '資料處理中，請稍後')
        } catch (error) {
          console.error('Loading 初始化失敗:', error)
          await this.$store.dispatch('app/showDownloadLoading', false)
        }
      } else {
        await this.$store.dispatch('app/showDownloadLoading', false)
      }
    },
  },
  computed: {
    chatroomRows() {
      return [
        {
          key: 'welcome',
          label: '開場白',
          type: 'textarea',
          rows: 3,
          model: this.form.welcomeMessages,
        },
        ...this.form.suggestedQuestions.map((item, index) => ({
          key: `question-${item.order}`,
          label: `常見問題${index + 1}`,
          type: 'textarea',
          rows: 2,
          model: item.translations,
        })),
      ]
    },
  },
  created() {
    this.getList()
  },
  methods: {
    async handleDefaultSetting() {
      this.listLoading = true
      await getAiAgentDefaultSetting()
        .then(res => {
          if (res.success && res.data) {
            const resData = res.data
            this.formByDefaultSetting.idleImageUrl = resData.idleImageUrl
            this.formByDefaultSetting.openingVideoUrl = resData.openingVideoUrl
            this.formByDefaultSetting.replyVideoUrl = resData.replyVideoUrl
            this.formByDefaultSetting.appAiUrl = resData.appAiUrl
            this.formByDefaultSetting.webAiUrl = resData.webAiUrl
          }
        })
        .finally(() => {
          this.dialogVisibleByDefaultSetting = true
          this.listLoading = false
        })
    },
    async saveByDefaultSetting() {
      this.listLoading = true
      await saveAiAgentDefaultSetting(this.formByDefaultSetting)
        .then(async res => {
          this.$message.success('設定保存成功')
          await this.initDefaultSetting()
        })
        .catch(err => {
          console.log(err)
          this.$message.error(`設定保存失敗: ${err}`)
        })
        .finally(() => {
          this.dialogVisibleByDefaultSetting = false
          this.listLoading = false
        })
    },
    async closeByDefaultSetting() {
      this.dialogVisibleByDefaultSetting = false
      await this.initDefaultSetting()
    },
    async initDefaultSetting() {
      this.formByDefaultSetting = {
        idleImageUrl: '',
        openingVideoUrl: '',
        replyVideoUrl: '',
        appAiUrl: '',
        webAiUrl: '',
      }
    },
    normalizeLocalizedMap(source) {
      const base = createLocalizedMap()
      if (!source) {
        return base
      }

      this.languageOptions.forEach(item => {
        base[item.code] = source[item.code] || ''
      })
      return base
    },

    applyDefaultWelcomeMessagesIfEmpty() {
      const hasAnyWelcomeMessage = this.languageOptions.some(item =>
        String(this.form.welcomeMessages[item.code] || '').trim()
      )

      if (!hasAnyWelcomeMessage) {
        this.form.welcomeMessages = {
          ...createLocalizedMap(),
          ...defaultWelcomeMessages,
        }
      }
    },

    normalizeSuggestedQuestions(source) {
      const current = Array.isArray(source) ? source : []
      return Array.from({ length: 5 }, (_, index) => {
        const matched =
          current.find(item => Number(item.order) === index + 1) || current[index] || {}
        return {
          order: index + 1,
          translations: this.normalizeLocalizedMap(matched.translations),
        }
      })
    },

    normalizeForm(source) {
      const base = createDefaultForm()
      const normalized = Object.assign(base, cloneDeep(source || {}))
      normalized.welcomeMessages = this.normalizeLocalizedMap(source && source.welcomeMessages)
      normalized.suggestedQuestions = this.normalizeSuggestedQuestions(
        source && source.suggestedQuestions
      )
      normalized.idleImageUrl = normalized.idleImageUrl || ''
      normalized.openingVideoUrl = normalized.openingVideoUrl || ''
      normalized.replyVideoUrl = normalized.replyVideoUrl || ''
      normalized.secret = normalized.secret || ''
      normalized.apiKey = normalized.apiKey || ''
      normalized.assistantsID = normalized.assistantsID || ''
      normalized.code = normalized.code || ''
      normalized.siteName = normalized.siteName || ''
      return normalized
    },

    buildPayload() {
      const payload = cloneDeep(this.form)
      payload.welcomeMessages = this.normalizeLocalizedMap(payload.welcomeMessages)
      payload.suggestedQuestions = this.normalizeSuggestedQuestions(payload.suggestedQuestions)
      return payload
    },

    async refresh() {
      this.listQuery = {
        search: '',
        status: null,
        page: 1,
        limit: 20,
        sort: '-id',
      }
      await this.getList()
    },

    async getList() {
      this.listLoading = true
      try {
        const response = await getAiAgentList(this.listQuery)
        if (response.success) {
          this.list = response.data
          this.total = response.total || response.data.length || 0
        } else {
          Message.error(response.message || '獲取資料失敗')
        }
      } catch (error) {
        Message.error((error.response && error.response.message) || '獲取資料失敗')
      }
      this.listLoading = false
    },

    handleCreate() {
      this.form = createDefaultForm()
      this.dialogVisible = true
    },

    async handleChatLog(row) {
      this.dialogRowIdByChatLog = row.id
      this.listQueryByChatLog.page = 1
      this.listQueryByChatLog.limit = 10
      this.listByChatLog = []
      await this.getChatLogsByAIAgentId(row.id)
      this.dialogVisibleByChatLog = true
    },

    handleEdit(row) {
      this.form = this.normalizeForm(row)
      this.dialogVisible = true
    },

    handleChatroom(row) {
      this.form = this.normalizeForm(row)
      this.applyDefaultWelcomeMessagesIfEmpty()
      this.chatDialogVisible = true
    },

    handleDelete(row) {
      MessageBox.confirm('確定要刪除此筆資料嗎？', '提示', {
        confirmButtonText: '確定',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(async () => {
        try {
          const response = await deleteAiAgent(row.id)
          if (response.success) {
            Message.success(response.message || '刪除成功')
            this.getList()
          } else {
            Message.error(response.message || '刪除失敗')
          }
        } catch (error) {
          Message.error((error.response && error.response.message) || '刪除失敗')
        }
      })
    },

    saveBasicData() {
      this.$refs.form.validate(async valid => {
        if (!valid) {
          return
        }

        await this.persistForm(() => {
          this.dialogVisible = false
        })
      })
    },

    async saveChatroomData() {
      if (!this.form.id) {
        Message.warning('請先建立場域基本資料後，再設定聊天室內容')
        return
      }

      await this.persistForm(() => {
        this.chatDialogVisible = false
      })
    },

    async persistForm(onSuccess) {
      try {
        this.dialogLoading = true
        const response = await saveAiAgent(this.buildPayload())
        if (response.success) {
          Message.success(response.message || '儲存成功')
          this.form = this.normalizeForm(response.data)
          if (typeof onSuccess === 'function') {
            onSuccess()
          }
          this.getList()
        } else {
          Message.error(response.message || '儲存失敗')
        }
      } catch (error) {
        Message.error((error.response && error.response.message) || '儲存失敗')
      } finally {
        this.dialogLoading = false
      }
    },

    async getChatLogsByAIAgentId(aiAgentId) {
      this.listLoading = true
      try {
        const response = await getAiAgentChatLogForPaged({
          ...this.listQueryByChatLog,
          id: aiAgentId,
        })
        if (response.success && response.data) {
          this.listByChatLog = response.data.data || []
          this.total = response.data.total || 0
        } else {
          Message.error(response.message || '獲取資料失敗')
        }
        this.listLoading = false
      } catch (error) {
        Message.error((error.response && error.response.message) || '獲取資料失敗')
      }
      this.listLoading = false
    },

    async exportToExcelByChatLog(aiAgentId) {
      this.listLoading = true

      try {
        const response = await downloadAiAgentChatLogsToExcel({
          id: aiAgentId,
        })

        let fileName = `AI客服問答紀錄匯出_${new Date().getTime()}.xlsx`
        const contentDisposition = response.headers['content-disposition']
        if (contentDisposition) {
          const fileNameMatch =
            contentDisposition.match(/filename\*=utf-8''(.+)/i) ||
            contentDisposition.match(/filename=(.+)/i)
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = decodeURIComponent(fileNameMatch[1])
          }
        }

        console.log(response)

        const blob = new Blob([response], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })

        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(blob, fileName)
        } else {
          const downloadElement = document.createElement('a')
          const href = window.URL.createObjectURL(blob)

          downloadElement.href = href
          downloadElement.download = fileName
          document.body.appendChild(downloadElement)
          downloadElement.click()

          document.body.removeChild(downloadElement)
          window.URL.revokeObjectURL(href)
        }

        this.$message({
          message: 'Excel 檔案下載成功！',
          type: 'success',
        })
      } catch (error) {
        console.error('匯出失敗:', error)
        this.$message.error('系統錯誤，無法匯出 Excel 檔案')
      } finally {
        this.listLoading = false
      }
    },
  },
}
</script>

<style>
.ai-agent-container .el-dialog__body {
  padding-top: 0px;
}

.chatroom-grid {
  display: grid;
  grid-template-columns: 140px repeat(11, minmax(140px, 1fr));
  gap: 8px;
  margin-top: 16px;
  overflow-x: auto;
}

.chatroom-grid__header,
.chatroom-grid__label {
  font-weight: 600;
  display: flex;
  align-items: center;
  min-height: 40px;
}

.chatroom-grid__header {
  padding: 8px;
  background: #f5f7fa;
  border-radius: 4px;
}

.chatroom-grid__label {
  padding: 8px;
  background: #fafafa;
  border-radius: 4px;
}

.chatroom-grid__cell .el-textarea__inner,
.chatroom-grid__cell .el-input__inner {
  min-height: 72px !important;
}

.header-item {
  justify-content: center;
}
</style>
